import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Radio } from "lucide-react";
import { useMyLiveStreams } from "@/hooks/useMyLiveStreams";
import { LiveStreamPlayer } from "@/components/liveStream/LiveStreamPlayer";
import type { LiveStreamDisplayStatus, MyLiveStreamEntry } from "@/types";

// Statuses where mounting the WHEP player makes sense (same set Team Live
// uses); everything else gets an info-only tile that keeps its slot visible.
const PLAYER_STATUSES = new Set<LiveStreamDisplayStatus>([
  "live",
  "starting",
  "stopping",
]);

function formatWindow(
  firstStartedAt: string | null,
  lastEndedAt: string | null,
): string {
  const opts: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  };
  const start = firstStartedAt
    ? new Date(firstStartedAt).toLocaleString(undefined, opts)
    : "—";
  const end = lastEndedAt
    ? new Date(lastEndedAt).toLocaleString(undefined, opts)
    : "—";
  return `${start} → ${end}`;
}

function StreamTile({ entry }: { entry: MyLiveStreamEntry }) {
  const { t } = useTranslation("pages");
  const { liveStream, device, session, whepUrl } = entry;
  const status = liveStream.displayStatus ?? "unknown";
  const canPlay = PLAYER_STATUSES.has(status) && !!whepUrl;

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
      {canPlay && whepUrl ? (
        <div className="aspect-video bg-black">
          <LiveStreamPlayer
            src={whepUrl}
            title={session?.title ?? device.name ?? t("liveStreams.unassigned")}
            startTime={liveStream.startedAt}
            isRunning={status === "live"}
            variant="fill"
          />
        </div>
      ) : (
        <div className="flex aspect-video flex-col items-center justify-center gap-2 bg-gray-50 text-gray-500 dark:bg-gray-900 dark:text-gray-400">
          <Radio className="h-6 w-6" aria-hidden="true" />
          <span className="text-sm">
            {t(`sessions.detail.liveStream.status.${status}`, {
              defaultValue: t("liveStreams.connecting"),
            })}
          </span>
        </div>
      )}
      <div className="flex items-center justify-between gap-2 px-3 py-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{device.name ?? device.id}</p>
          <p className="truncate text-xs text-gray-500">
            {session ? session.title : t("liveStreams.unassigned")}
          </p>
        </div>
        {status === "live" && (
          <span className="shrink-0 rounded bg-red-600 px-1.5 py-0.5 text-xs font-semibold text-white">
            {t("liveStreams.live")}
          </span>
        )}
      </div>
    </div>
  );
}

export default function LiveStreamsPage() {
  const { t } = useTranslation("pages");
  const { data, isLoading, isError } = useMyLiveStreams();
  const active = data?.active ?? [];
  const recent = data?.recent ?? [];
  const isEmpty =
    !isLoading && !isError && active.length === 0 && recent.length === 0;

  return (
    <div className="mx-auto max-w-3xl p-4" data-testid="live-streams-page">
      <div className="mb-4 flex items-center gap-2">
        <Radio className="h-6 w-6" aria-hidden="true" />
        <h1 className="text-2xl font-bold">{t("liveStreams.title")}</h1>
      </div>
      <p className="mb-6 text-sm text-gray-500">{t("liveStreams.subtitle")}</p>

      {isLoading && (
        <div className="py-10 text-center text-sm text-gray-500">…</div>
      )}

      {isError && (
        <div className="py-10 text-center text-sm text-red-500">
          {t("common:errors.generic")}
        </div>
      )}

      {isEmpty && (
        <div className="rounded-lg border border-dashed border-gray-300 p-10 text-center dark:border-gray-600">
          <Radio className="mx-auto mb-2 h-8 w-8 text-gray-400" aria-hidden="true" />
          <p className="font-medium">{t("liveStreams.empty")}</p>
          <p className="mt-1 text-sm text-gray-500">
            {t("liveStreams.emptyHint")}
          </p>
        </div>
      )}

      {active.length > 0 && (
        <section className="mb-8" aria-label={t("liveStreams.liveSection")}>
          {/* Multi-cam: every angle plays at once; one angle is full-width. */}
          <div className={active.length > 1 ? "grid gap-4 sm:grid-cols-2" : ""}>
            {active.map((entry) => (
              <StreamTile key={entry.liveStream.id} entry={entry} />
            ))}
          </div>
        </section>
      )}

      {recent.length > 0 && (
        <section aria-label={t("liveStreams.recentSection")}>
          <p className="section-title">{t("liveStreams.recentSection")}</p>
          <ul className="space-y-2">
            {recent.map((entry) => (
              <li
                key={entry.liveStream.id}
                className="flex items-center justify-between gap-2 rounded-lg border border-gray-200 px-4 py-3 dark:border-gray-700"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">
                    {entry.session
                      ? entry.session.title
                      : t("liveStreams.unassigned")}
                  </p>
                  <p className="text-xs text-gray-500">
                    {formatWindow(
                      entry.liveStream.startedAt ?? null,
                      entry.liveStream.endedAt ?? null,
                    )}
                  </p>
                </div>
                {entry.session ? (
                  <Link
                    to={`/watch/${entry.session.id}`}
                    aria-label={`${t("liveStreams.watch")} — ${entry.session.title}`}
                    className="shrink-0 rounded bg-blue-600 px-3 py-1.5 text-sm text-white"
                  >
                    {t("liveStreams.watch")}
                  </Link>
                ) : (
                  <span className="shrink-0 rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-500 dark:bg-gray-800">
                    {t("liveStreams.ended")}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
